import { useRef, useCallback, useState, useEffect } from 'react';

const WS_URL = process.env.REACT_APP_PUBLIC_AI_WS_URL || '';
const API_KEY = process.env.REACT_APP_PUBLIC_AI_API_KEY || '';
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

const RECONNECT_BASE_DELAY = 3000;
const RECONNECT_MAX_DELAY = 30000;

/**
 * Request a Cloudflare Turnstile challenge token.
 *
 * ⚠️ Los tokens de Turnstile son DE UN SOLO USO: en cuanto el authorizer los
 * canjea contra /siteverify quedan gastados, y reenviarlos devuelve
 * success:false. Por eso aquí NO se cachea el token para reutilizarlo: se
 * mantiene solo el que se pre-carga al abrir la página (para que la PRIMERA
 * conexión sea instantánea) y se CONSUME al entregarlo. Cada reconexión pide
 * uno nuevo.
 *
 * La versión anterior lo cacheaba 4 minutos y lo reutilizaba: mientras la
 * verificación estuvo desactivada en el backend daba igual, pero al reactivarla
 * (25-jul-2026) toda reconexión dentro de esa ventana era denegada con el token
 * ya gastado — visible en los logs del authorizer como un "Authorized" seguido
 * de varios "Denied: invalid Turnstile token".
 */
let prefetchedToken = null;
let tokenPromise = null;

async function getTurnstileToken() {
  // Consumir el token pre-cargado, si lo hay: se entrega UNA sola vez
  if (prefetchedToken) {
    const token = prefetchedToken;
    prefetchedToken = null;
    return token;
  }

  // Si ya hay una petición en vuelo, esperarla en vez de lanzar otra
  if (tokenPromise) return tokenPromise;

  tokenPromise = fetchTurnstileToken();
  try {
    return await tokenPromise;
  } finally {
    tokenPromise = null;
  }
}

async function fetchTurnstileToken() {
  if (!window.turnstile || !TURNSTILE_SITE_KEY) return '';

  return new Promise((resolve) => {
    try {
      window.turnstile.execute(TURNSTILE_SITE_KEY, {
        callback: (token) => resolve(token),
        'error-callback': () => resolve(''),
        'expired-callback': () => resolve(''),
      });
    } catch {
      try {
        let container = document.getElementById('turnstile-container');
        if (!container) {
          container = document.createElement('div');
          container.id = 'turnstile-container';
          container.style.display = 'none';
          document.body.appendChild(container);
        } else {
          try { window.turnstile.remove(container); } catch { /* ignore */ }
        }
        window.turnstile.render(container, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token) => resolve(token),
          'error-callback': () => resolve(''),
          'expired-callback': () => resolve(''),
        });
      } catch {
        resolve('');
      }
    }
  });
}

// Pre-fetch token on module load (runs when page loads) para que la primera
// conexión no espere al reto. Queda guardado hasta que alguien lo consuma.
if (typeof window !== 'undefined') {
  setTimeout(() => {
    fetchTurnstileToken().then((token) => {
      if (token) prefetchedToken = token;
    });
    // Los tokens caducan a los ~5 min: si nadie ha abierto el chat para
    // entonces, se descarta y la conexión pedirá uno nuevo.
    setTimeout(() => { prefetchedToken = null; }, 240000);
  }, 1000);
}

/**
 * WebSocket hook for the public chat widget.
 *
 * @param {Function} onMessage - callback receiving parsed { type, data } messages
 * @returns {{ connect, disconnect, send, connectionStatus }}
 */
export function useChatWebSocket(onMessage) {
  const [connectionStatus, setConnectionStatus] = useState('disconnected'); // connected | disconnected | reconnecting
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const intentionalCloseRef = useRef(false);
  const onMessageRef = useRef(onMessage);

  // Keep onMessage ref current without re-creating connect
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  const stopReconnect = useCallback(() => {
    if (reconnectRef.current) {
      clearTimeout(reconnectRef.current);
      reconnectRef.current = null;
    }
  }, []);

  const scheduleReconnect = useCallback(() => {
    stopReconnect();
    const attempt = reconnectAttemptRef.current;
    const delay = Math.min(RECONNECT_BASE_DELAY * Math.pow(2, attempt), RECONNECT_MAX_DELAY);
    reconnectAttemptRef.current = attempt + 1;
    setConnectionStatus('reconnecting');

    reconnectRef.current = setTimeout(() => {
      // Re-connect (connectInner will be called)
      connectInner();
    }, delay);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopReconnect]);

  const connectInner = useCallback(async () => {
    if (!WS_URL) {
      console.warn('REACT_APP_PUBLIC_AI_WS_URL not configured');
      return;
    }

    // Close existing connection if any
    if (wsRef.current) {
      try { wsRef.current.close(); } catch { /* ignore */ }
    }

    setConnectionStatus('reconnecting');

    // Get Turnstile challenge token
    const challengeToken = await getTurnstileToken();

    // Si el sitio tiene Turnstile configurado pero no hemos conseguido reto, el
    // authorizer va a denegar seguro: no abrimos un socket condenado (gastaría
    // un intento y dispararía otro ciclo de reconexión), reintentamos con el
    // backoff que ya tenemos.
    if (TURNSTILE_SITE_KEY && !challengeToken) {
      console.warn('No Turnstile token available; retrying');
      scheduleReconnect();
      return;
    }

    // Build WebSocket URL with query params
    const params = new URLSearchParams();
    if (API_KEY) params.set('apiKey', API_KEY);
    if (challengeToken) params.set('challengeToken', challengeToken);
    const qs = params.toString();
    const url = qs ? `${WS_URL}?${qs}` : WS_URL;

    const ws = new WebSocket(url);

    ws.onopen = () => {
      setConnectionStatus('connected');
      reconnectAttemptRef.current = 0;

      // Request conversation history
      ws.send(JSON.stringify({ action: 'getHistory', data: {} }));
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (onMessageRef.current) {
          onMessageRef.current(msg);
        }
      } catch (err) {
        console.error('Error parsing WS message:', err);
      }
    };

    ws.onclose = () => {
      setConnectionStatus('disconnected');

      // Auto-reconnect unless intentionally closed
      if (!intentionalCloseRef.current) {
        scheduleReconnect();
      }
    };

    ws.onerror = (err) => {
      console.error('WebSocket error:', err);
    };

    wsRef.current = ws;
  }, [scheduleReconnect]);

  const connect = useCallback(() => {
    intentionalCloseRef.current = false;
    reconnectAttemptRef.current = 0;
    connectInner();
  }, [connectInner]);

  const disconnect = useCallback(() => {
    intentionalCloseRef.current = true;
    stopReconnect();
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setConnectionStatus('disconnected');
  }, [stopReconnect]);

  const send = useCallback((data) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    } else {
      console.warn('WebSocket not connected, cannot send');
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      intentionalCloseRef.current = true;
      stopReconnect();
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [stopReconnect]);

  return { connect, disconnect, send, connectionStatus };
}
